import axios, { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';

import { HttpMethod } from '@/api/HttpMethod.model.ts';
import { getCsrfToken } from '@/api/utils/getCsrfTokenRequest.util.ts';

import { HTTP_STATUS } from './ErrorCode.model';
import { getErrorResponseInterceptor } from './getErrorResponseInterceptor.util.ts';
import { HTTP_HEADER } from './httpHeaders.model';

const successfulResponseHandler = (response: AxiosResponse): AxiosResponse => {
  if (response.status === HTTP_STATUS.REDIRECT) {
    delete response.headers[HTTP_HEADER['X-CSRF-Token']];
  }

  return response;
};

type Return = ReturnType<typeof axios.create>;

export const useAxiosInstance = (): Return => {
  const { i18n } = useTranslation();

  // withCredentials: wysyłaj/przyjmuj cookie sesji także cross-origin (lokalnie FE :3000 -> BE :8080).
  // Bez tego cookie sesji nie krąży i stan po stronie sesji (np. impersonacja) nie przeżywa między żądaniami.
  // W prod (same-origin) bez zmian; CORS po stronie BE ma allow-credentials=true + konkretny origin.
  const axiosInstance = axios.create({ withCredentials: true });

  axiosInstance.interceptors.response.use(
    successfulResponseHandler,
    getErrorResponseInterceptor(axiosInstance),
  );

  axiosInstance.interceptors.request.use(
    async config => {
      if (
        config.method &&
        [HttpMethod.PUT, HttpMethod.POST, HttpMethod.PATCH, HttpMethod.DELETE].some(
          method => method === config.method,
        )
      ) {
        const token = await getCsrfToken();
        config.headers[HTTP_HEADER['X-CSRF-Token']] = token;
      } else {
        delete config.headers[HTTP_HEADER['X-CSRF-Token']];
      }
      config.params ??= {};
      const detectedLanguage = i18n.language.substring(0, 2).toUpperCase();
      const supportedLanguages = ['EN', 'PL'];
      config.params['lang'] = supportedLanguages.includes(detectedLanguage)
        ? detectedLanguage
        : 'EN';
      return config;
    },
    (error: unknown) => {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  );

  return axiosInstance;
};
